import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Alert, Box, Button, Paper, Stack, Typography } from '@mui/material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';
import BrokerAreaPicker from '../components/BrokerAreaPicker';
import { formatPersonName } from '../utils/personName';

const FONT = "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";
const GOLD = '#9d6f32';
const Field = ({id, label, value, onChange, type = 'text', required = false,
  disabled = false, min, max, onBlur}: {
  id: string; label: string; value: string; onChange?: (value: string) => void;
  type?: React.HTMLInputTypeAttribute; required?: boolean; disabled?: boolean;
  min?: number; max?: number; onBlur?: () => void;
}) => <Box sx={{width: '100%', minWidth: 0}}>
  <Typography component="label" htmlFor={id} sx={{display: 'block', mb: 0.75,
    fontFamily: FONT, fontSize: 13, fontWeight: 600, color: '#5b5b5b'}}>
    {label}{required ? ' *' : ''}
  </Typography>
  <Box component="input" id={id} type={type} value={value} required={required}
    disabled={disabled} min={min} max={max} autoCapitalize={type === 'text' ? 'words' : undefined}
    onChange={(event: React.ChangeEvent<HTMLInputElement>) => onChange?.(event.target.value)}
    onBlur={onBlur}
    sx={{width: '100%', boxSizing: 'border-box', height: 46, px: 1.5,
      border: '1px solid #d9d3c8', borderRadius: 2.5, bgcolor: disabled ? '#f7f5f1' : '#fff',
      color: disabled ? '#777' : '#1c1b19', fontFamily: FONT, fontSize: 16,
      outline: 'none', '&:focus': {borderColor: GOLD, boxShadow: '0 0 0 1px #9d6f32'}}} />
</Box>;

type Member = { name: string; phone: string; role: string };
type Profile = {
  name: string; agencyName: string; email: string; dateOfBirth: string;
  city: string; area: string; reraNumber: string;
  teamSize: string; yearsExperience: string; interestedProjectIds: string[];
  members: Member[];
};
const emptyProfile: Profile = {
  name: '', agencyName: '', email: '', dateOfBirth: '', city: '', area: '',
  reraNumber: '', teamSize: '', yearsExperience: '', interestedProjectIds: [], members: [],
};

export default function BrokerProfilePage() {
  const { token = '' } = useParams();
  const previewMode = process.env.NODE_ENV === 'development' && token === 'preview';
  const formRef = useRef<HTMLFormElement>(null);
  const [profile, setProfile] = useState<Profile>(emptyProfile);
  const [phone, setPhone] = useState('');
  const [projects, setProjects] = useState<Array<{id: string; name: string}>>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => {
    if (previewMode) {
      setProfile({...emptyProfile, name: 'Sample Broker'});
      setPhone('••••••1234');
      setProjects([
        {id: 'preview-avsar', name: 'Swarna Avsar'},
        {id: 'preview-lagom', name: 'Swarna Lagom'},
      ]);
      setLoading(false);
      return;
    }
    let active = true;
    httpsCallable(functions, 'brokerProfileForm')({token, action: 'load'})
      .then((result: any) => {
        if (!active) return;
        const data = result.data || {};
        setProfile({
          ...emptyProfile,
          name: String(data.name || ''),
          agencyName: String(data.agencyName || ''),
        });
        setPhone(String(data.phoneLabel || ''));
        setProjects(Array.isArray(data.projects) ? data.projects : []);
      })
      .catch((err) => active && setError(err?.message || 'This link is unavailable.'))
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [token, previewMode]);
  const change = (key: keyof Omit<Profile, 'members'>, value: string) =>
    setProfile((old) => ({...old, [key]: value}));
  const updateMember = (index: number, key: keyof Member, value: string) =>
    setProfile((old) => ({...old, members: old.members.map((m, i) =>
      i === index ? {...m, [key]: value} : m)}));
  const submit = async () => {
    if (!formRef.current?.reportValidity()) return;
    setError('');
    if (profile.name.trim().length < 2 || profile.name.includes('--')) {
      setError('Enter your full name without --.');
      return;
    }
    setSaving(true);
    try {
      await httpsCallable(functions, 'brokerProfileForm')({
        token, action: 'submit', profile: {
          ...profile,
          members: (Number(profile.teamSize) > 1 ? profile.members : []).map((member) => ({
            ...member, phone: member.phone.replace(/\D/g, ''),
          })),
        },
      });
      setDone(true);
    } catch (err: any) {
      setError(err?.message || 'Could not submit your details.');
    } finally {
      setSaving(false);
    }
  };
  return <Box sx={{minHeight: '100vh', bgcolor: '#f7f5f1', p: {xs: 1.5, sm: 2},
    fontFamily: FONT}}>
    <Paper elevation={0} sx={{maxWidth: 660, mx: 'auto', my: {xs: 1, sm: 3},
      border: '1px solid #e8e3da', borderRadius: 4, overflow: 'hidden'}}>
      <Box sx={{px: {xs: 2.5, sm: 4}, py: {xs: 2.5, sm: 3},
        borderBottom: '1px solid #eee6da', bgcolor: '#fbf8f3'}}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Box component="img" src="/logo192.png" alt="Swarna Group logo"
            sx={{width: 52, height: 52, objectFit: 'contain'}} />
          <Box>
            <Typography sx={{fontFamily: 'Georgia, serif', fontWeight: 700,
              fontSize: {xs: 23, sm: 27}, lineHeight: 1.1}}>Swarna Group</Typography>
            <Typography variant="caption" sx={{letterSpacing: 2, color: '#9d6f32', fontWeight: 700}}>
              BROKER NETWORK
            </Typography>
          </Box>
        </Stack>
        <Typography component="h1" sx={{mt: 2, fontWeight: 700, fontSize: 16, color: '#1c1b19'}}>
          Complete your profile
        </Typography>
      </Box>
      <Box sx={{p: {xs: 2.5, sm: 4}}}>
      {loading ? <Typography>Loading your form…</Typography> : done ?
        <Alert severity="success">Thank you. Your details have been sent to our team for review.</Alert> :
        error && !phone ? <Alert severity="error">{error}</Alert> :
        <Box component="form" ref={formRef} onSubmit={(event) => event.preventDefault()}>
          {previewMode && <Alert severity="info" sx={{mb: 2}}>
            Local preview. You can explore the form, but it will not send details.
          </Alert>}
          {error && <Alert severity="error" sx={{mb: 2}}>{error}</Alert>}
          <Stack spacing={2.25}>
            <Typography fontWeight={700} color="#714c1e">Your details</Typography>
            <Field id="bp-mobile" label="Mobile number" value={phone} disabled />
            <Field id="bp-name" label="Name" value={profile.name} required
              onChange={(value) => change('name', value)}
              onBlur={() => change('name', formatPersonName(profile.name))} />
            <Field id="bp-agency" label="Agency name (if any)" value={profile.agencyName}
              onChange={(value) => change('agencyName', value)} />
            <Field id="bp-email" label="Email" type="email" value={profile.email}
              onChange={(value) => change('email', value)} />
            <Field id="bp-dob" label="Date of birth (optional)" type="date" value={profile.dateOfBirth}
              onChange={(value) => change('dateOfBirth', value)} />
            <Stack direction={{xs: 'column', sm: 'row'}} spacing={2}>
              <Field id="bp-city" label="City" value={profile.city}
                onChange={(value) => change('city', value)} />
              <BrokerAreaPicker value={profile.area}
                onChange={(value) => change('area', value)} frontDeskStyle />
            </Stack>
            <Field id="bp-rera" label="RERA number (if applicable)" value={profile.reraNumber}
              onChange={(value) => change('reraNumber', value)} />
            {projects.length > 0 && <Box>
              <Typography fontWeight={700} sx={{mb: 1}}>Projects you are interested in</Typography>
              <Stack direction="row" flexWrap="wrap" gap={1}>{projects.map((project) =>
                <Button key={project.id} variant={profile.interestedProjectIds.includes(project.id) ?
                  'contained' : 'outlined'} sx={{borderRadius: 999, minHeight: 44,
                  borderColor: '#d9d3c8', textTransform: 'none',
                  bgcolor: profile.interestedProjectIds.includes(project.id) ? GOLD : '#fff',
                  color: profile.interestedProjectIds.includes(project.id) ? '#fff' : '#1c1b19',
                  '&:hover': {bgcolor: profile.interestedProjectIds.includes(project.id) ? '#815926' : '#faf8f4'}}}
                  onClick={() => setProfile((old) => ({...old,
                  interestedProjectIds: old.interestedProjectIds.includes(project.id) ?
                    old.interestedProjectIds.filter((id) => id !== project.id) :
                    [...old.interestedProjectIds, project.id],
                }))}>{project.name}</Button>)}</Stack>
            </Box>}
            <Stack direction={{xs: 'column', sm: 'row'}} spacing={2}>
              <Field id="bp-team-size" label="Team size" type="number" min={0} max={500}
                value={profile.teamSize} onChange={(value) => change('teamSize', value)} />
              <Field id="bp-experience" label="Years of experience" type="number" min={0} max={80}
                value={profile.yearsExperience} onChange={(value) => change('yearsExperience', value)} />
            </Stack>
            {Number(profile.teamSize) > 1 && <Box component="section" aria-label="Team members"
              sx={{p: {xs: 1.75, sm: 2}, border: '1px solid #e8e3da',
                borderRadius: 3, bgcolor: '#fbf9f5'}}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{mb: 1.5}}>
                <Typography variant="subtitle1" fontWeight={700} color="#714c1e">
                  Team members
                </Typography>
                {profile.members.length > 0 && <Typography variant="caption" color="text.secondary">
                  {profile.members.length} added
                </Typography>}
              </Stack>
              <Stack spacing={1.5}>
                {profile.members.map((member, index) => <Box key={index}
                  sx={{p: 2, border: '1px solid #e8e3da', borderRadius: 2.5, bgcolor: '#fff'}}>
                  <Stack spacing={1.5}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                      <Typography fontWeight={700} fontSize={14}>Member {index + 1}</Typography>
                      <Button type="button" size="small" startIcon={<DeleteOutlineRoundedIcon />}
                        onClick={() => setProfile((old) => ({...old,
                          members: old.members.filter((_, i) => i !== index)}))}
                        sx={{textTransform: 'none', color: '#8b4d3a'}}>
                        Remove
                      </Button>
                    </Stack>
                    <Field id={`bp-member-name-${index}`} label="Member name" value={member.name} required
                      onChange={(value) => updateMember(index, 'name', value)} />
                    <Field id={`bp-member-mobile-${index}`} label="Member mobile" type="tel"
                      value={member.phone} required onChange={(value) => updateMember(index, 'phone', value)} />
                    <Field id={`bp-member-role-${index}`} label="Role (optional)" value={member.role}
                      onChange={(value) => updateMember(index, 'role', value)} />
                  </Stack>
                </Box>)}
                <Button type="button" variant="outlined" startIcon={<AddRoundedIcon />}
                  disabled={profile.members.length >= 30}
                  onClick={() => setProfile((old) => ({
                    ...old, members: [...old.members, {name: '', phone: '', role: ''}],
                  }))}
                  sx={{minHeight: 48, borderStyle: 'dashed', borderRadius: 2.5,
                    borderColor: '#bd9a69', color: '#815926', bgcolor: '#fff',
                    textTransform: 'none', fontWeight: 700,
                    '&:hover': {borderStyle: 'dashed', borderColor: GOLD, bgcolor: '#fffaf2'}}}>
                  Add team member
                </Button>
              </Stack>
            </Box>}
            <Button type="button" onClick={submit} variant="contained" disabled={saving || previewMode}
              size="large" sx={{bgcolor: '#9d6f32', py: 1.4,
                '&:hover': {bgcolor: '#815926'}}}>
              {saving ? 'Submitting…' : 'Submit details'}
            </Button>
          </Stack>
        </Box>}
      </Box>
    </Paper>
  </Box>;
}
